<template>
  <div class="daerah-strata">
    <v-snackbar
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      v-model="snackbar.show"
    >
      {{ snackbar.message }}
    </v-snackbar>

    <PageHeader
      icon="mdi-map-marker-radius-outline"
      title="Pendapatan Isi Rumah Kasar Bulanan mengikut Daerah Pentadbiran & Strata"
      subtitle="Mengikut daerah pentadbiran dan strata"
    />

    <!-- Detailed table -->
    <v-card outlined class="mb-4">
      <v-card-title class="py-3 d-flex align-center flex-wrap">
        <v-icon small class="mr-2">mdi-table</v-icon>
        <span class="subtitle-1 font-weight-medium">Pendapatan ikut Daerah Pentadbiran &amp; Strata</span>
        <v-chip x-small color="grey lighten-3" class="ml-3 font-weight-medium">{{ scopeName }}</v-chip>
        <v-spacer></v-spacer>
        <v-btn color="green darken-2" dark class="mr-2 text-none" :disabled="!hasData" :loading="exporting" @click="exportExcel">
          <v-icon left>mdi-microsoft-excel</v-icon>
          Excel
        </v-btn>
        <v-btn color="red darken-1" dark class="text-none" :disabled="!hasData" :loading="exportingPdf" @click="exportPDF">
          <v-icon left>mdi-file-pdf-box</v-icon>
          PDF
        </v-btn>
      </v-card-title>

      <!-- Filters -->
      <!-- Setiap penapis memakai susunan yang SAMA: label eyebrow di atas,
           kawalan di bawah. `align="end"` menjajarkan semua kawalan pada garis
           dasar yang sama walaupun tinggi labelnya berbeza. -->
      <v-card-text class="filter-bar py-3">
        <v-row align="end" dense>
          <v-col cols="12" sm="3">
            <div class="fld__label">Negeri</div>
            <v-select
              v-model="negeri"
              :items="negeriList"
              :disabled="negeriLocked"
              outlined dense hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="auto">
            <div class="fld__label">Pusingan</div>
            <div class="d-flex align-center pusingan-range">
              <v-select v-model="roundRange" :items="roundRangeItems" outlined dense hide-details class="pusingan-sel"></v-select>
            </div>
          </v-col>
          <!-- <v-col cols="12" sm="3">
            <div class="d-flex align-center" style="height:40px">
              <span class="text-caption text--secondary mr-3" style="white-space:nowrap">Sampel</span>
              <v-btn-toggle v-model="sampel" mandatory color="primary" class="data-toggle">
                <v-btn value="1" height="40" class="text-none px-4">1</v-btn>
                <v-btn value="2" height="40" class="text-none px-4">2</v-btn>
                <v-btn value="3" height="40" class="text-none px-4">Semua</v-btn>
              </v-btn-toggle>
            </div>
          </v-col> -->
          <!-- Data weighting toggle — UI only for now; wire to backend later -->
          <v-col cols="12" sm="auto">
            <div class="fld__label">Data</div>
            <!-- `color="primary"` digugurkan: primary Vuetify = biru, yang
                 bertentangan dengan sistem teal. Keadaan aktif diwarnakan
                 melalui CSS (.data-toggle) sebaliknya. -->
            <!-- 38, bukan 40: + birai 1px atas/bawah = tepat 40px, sama tinggi
                 dengan medan outlined di sebelahnya, supaya label "DATA" duduk
                 pada garis yang sama dengan "NEGERI"/"PUSINGAN". -->
            <v-btn-toggle v-model="dataWajaran" mandatory class="data-toggle">
              <v-btn value="W" height="38" class="text-none px-4">Wajaran</v-btn>
              <v-btn value="TW" height="38" class="text-none px-4">Tanpa Wajaran</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="auto" class="d-flex align-center">
            <v-btn outlined height="40" class="text-none reset-btn" @click="resetFilters">
              <v-icon small left>mdi-refresh</v-icon>
              Tetapkan Semula
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text class="d-flex align-center flex-wrap py-2">
        <v-spacer></v-spacer>
        <span class="penapis-ringkas text--secondary">{{ filterSummary }}</span>
      </v-card-text>

      <!-- Empty state -->
      <div v-if="loading" class="empty-state text-center py-12 px-4">
        <v-progress-circular indeterminate color="primary" size="48" width="4"></v-progress-circular>
        <div class="subtitle-1 font-weight-medium grey--text text--darken-1 mt-3">Memuatkan data…</div>
      </div>
      <div v-else-if="!hasData" class="empty-state text-center py-12 px-4">
        <v-icon size="56" color="grey lighten-1">mdi-database-off-outline</v-icon>
        <div class="subtitle-1 font-weight-medium grey--text text--darken-1 mt-3">Tiada rekod dijumpai</div>
        <div class="penapis-ringkas grey--text mt-1">Tiada data bagi penapis semasa ({{ filterSummary }}). Sila ubah pilihan penapis.</div>
      </div>

      <template v-else>
      <v-simple-table class="ks-table">
        <thead>
          <!-- Row 1: Daerah Pentadbiran + per-year banners + CAGR banner -->
          <tr>
            <th class="cat" rowspan="3">Daerah Pentadbiran</th>
            <th v-for="y in YEARS" :key="'y' + y" :colspan="3" rowspan="2">{{ y }} (RM)</th>
            <th :colspan="cagrColCount">Kadar Pertumbuhan Tahunan Dikompaun (CAGR) (%)</th>
          </tr>
          <!-- Row 2: CAGR year-pair sub-banners (each spans its 3 strata) -->
          <tr>
            <template v-if="hasCagr">
              <th v-for="p in cagrPairs" :key="'cp' + p.from + p.to" :colspan="3" class="cagr-sub">{{ p.from }} - {{ p.to }}</th>
            </template>
            <th v-else :colspan="3" class="cagr-sub">—</th>
          </tr>
          <!-- Row 3: Bandar / Luar Bandar / Jumlah under each year AND each CAGR pair -->
          <tr>
            <th v-for="col in strataHeaderCells" :key="col.key" class="strata-sub">{{ col.label }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="sec in sections">
            <tr :key="sec.key + '-h'" class="section">
              <td class="cat" :colspan="colCount">{{ sec.title }}</td>
            </tr>
            <tr
              v-for="r in sec.rows"
              :key="sec.key + '-' + (r.dp || 'total')"
              :class="{ total: r.isTotal, sub: !r.isTotal }"
            >
              <td class="cat">{{ r.daerah }}</td>
              <td
                v-for="col in dataCells(r)"
                :key="col.key"
                :class="{ 'cagr-empty': col.empty }"
              >{{ col.text }}</td>
            </tr>
            <tr :key="sec.key + '-sp'" class="spacer"><td :colspan="colCount"></td></tr>
          </template>
        </tbody>
      </v-simple-table>

      <div class="foot penapis-ringkas text--secondary px-4 py-3 d-flex justify-space-between flex-wrap">
        <span>{{ scopeName }}</span>
        <span>{{ filterSummary }}</span>
      </div>
      </template>
    </v-card>
  </div>
</template>

<script>
import { addJataHeaderExcel, addJataHeaderPdf } from "@/services/exportHeader";
import { T, loadTheme, argb, rgb, dark } from "@/services/theme";
import hiesWatermark from "@/assets/HIES watermark.png";
import DaerahXstrataServices from "@/services/daerahXstrata";
import { surveyTitle } from "@/services/surveyYear";
import { negeriStore, loadNegeriList } from "@/services/negeriList";
import getPusingan from "@/services/pusingan";
import { negeriLock } from "@/services/negeriScope";

const svc = new DaerahXstrataServices();
const pusinganSvc = new getPusingan();

// Negeri dropdown labels come from lookupnegeri via the shared negeriList
// service (negeriStore.list — reactive, cached, with offline fallback).

// build the CAGR pair list from an ordered array of year numbers
function cagrPairsFrom(years) {
  return years.slice(0, -1).map((from, i) => {
    const to = years[i + 1];
    return { from, to, years: to - from };
  });
}
// Returns null (→ dash) when an endpoint is missing/null so a pair touching an
// empty year shows "—" instead of a bogus 0%.
function cagr(start, end, years) {
  if (start == null || end == null || start <= 0 || end <= 0) return null;
  return (Math.log(end / start) / years) * 100;
}
// CAGR mirrors the year columns: its own Bandar/Luar Bandar/Jumlah per year-pair,
// keyed "<pair>|<strata>".
const STRATA_KEYS = ["Bandar", "Luar Bandar", "Jumlah"];
function cagrMapByStrata(strata, pairs) {
  const out = {};
  pairs.forEach(p => {
    STRATA_KEYS.forEach(s => {
      const vals = strata[s] || {};
      out[p.from + "-" + p.to + "|" + s] = cagr(vals[p.from], vals[p.to], p.years);
    });
  });
  return out;
}

function colLetter(n) {
  let s = "";
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
  return s;
}
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some(s => s.src === src)) return resolve();
    const el = document.createElement("script");
    el.src = src; el.onload = () => resolve(); el.onerror = () => reject(new Error("Gagal memuatkan " + src));
    document.head.appendChild(el);
  });
}
// load an image asset → { dataUrl(PNG base64), width, height }
// alpha (0-1, optional): bake transparency into the pixels — used for watermarks,
// since jsPDF's addImage has no opacity option of its own
function loadImage(url, alpha) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      const c2d = canvas.getContext("2d");
      if (alpha != null) c2d.globalAlpha = alpha;
      c2d.drawImage(img, 0, 0);
      let dataUrl; try { dataUrl = canvas.toDataURL("image/png"); } catch (e) { dataUrl = url; }
      resolve({ dataUrl, width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject; img.src = url;
  });
}

export default {
  name: "DaerahXstrataJR2",
  data() {
    return {
      svc,
      years: [],
      cagrPairs: [],
      api: {},
      loading: true,   // start loading — page fetches on created()
      snackbar: { show: false, message: "", color: "success", timeout: 3000 },
      // A negeri is REQUIRED for this page; default to Johor.
      negeri: "01",
      negeriLocked: false,    // true for PP/PO/FE → negeri pinned to own kodneg
      sampel: "3",
      dataWajaran: "W",       // "W" (Wajaran) / "TW" (Tanpa Wajaran) — UI only, wire to backend later
      // Active pusingan only — loaded in created() from getAllPusingan() filtered
      // by status == 1 (same rule as the Wajaran / Tapis Pusingan view). Falls back
      // to 1..12 if the service is unavailable.
      roundItems: [],
      // Pusingan range filter (From–To). Defaults to first–last available in
      // loadActiveRounds(). selectedRoundsModel is derived from this range.
      roundFrom: null,
      roundTo: null,
      exporting: false,
      exportingPdf: false,
    };
  },
  computed: {
    todayStr() { return new Date().toISOString().slice(0, 10); },
    YEARS() { return this.years; },
    hasCagr() { return this.cagrPairs.length > 0; },
    cagrColCount() { return Math.max(1, this.cagrPairs.length) * 3; },
    cagrExportCols() {
      const out = [];
      if (this.cagrPairs.length) {
        this.cagrPairs.forEach(p => {
          STRATA_KEYS.forEach(s => out.push({
            pairLabel: `${p.from} - ${p.to}`,
            strata: s,
            value: row => row.cagr[p.from + "-" + p.to + "|" + s],
          }));
        });
      } else {
        STRATA_KEYS.forEach(s => out.push({ pairLabel: "—", strata: s, value: () => null }));
      }
      return out;
    },
    // Daerah + (3 strata cols per year) + CAGR columns (pairs×3, ≥3)
    colCount() { return 1 + this.years.length * 3 + this.cagrColCount; },
    strataHeaderCells() {
      const cells = [];
      this.years.forEach(y => {
        STRATA_KEYS.forEach(s => cells.push({ key: "y" + y + s, label: s }));
      });
      this.cagrExportCols.forEach((c, i) => cells.push({ key: "c" + i, label: c.strata }));
      return cells;
    },
    hasData() {
      return this.years.length > 0 &&
        ((this.api.purata || []).length > 0 || (this.api.penengah || []).length > 0);
    },
    // No "Semua Negeri" — a negeri must be picked for the daerah breakdown.
    negeriList() {
      // Locked roles (PP/PO/FE) only see their own negeri.
      if (this.negeriLocked) {
        return negeriStore.list.filter(n => n.id === this.negeri).map(n => ({ text: n.nama, value: n.id }));
      }
      return negeriStore.list.map(n => ({ text: n.nama, value: n.id }));
    },
    negeriName() { const m = {}; negeriStore.list.forEach(n => (m[n.id] = n.nama)); return m; },
    selectedRoundsModel() {
      if (this.roundFrom == null || this.roundTo == null) return [];
      const lo = Math.min(this.roundFrom, this.roundTo);
      const hi = Math.max(this.roundFrom, this.roundTo);
      return this.roundItems.filter(p => p >= lo && p <= hi);
    },
    selectedRounds() { return [...this.selectedRoundsModel].sort((a, b) => a - b); },
    allRounds() { return this.roundItems.length > 0 && this.selectedRoundsModel.length === this.roundItems.length; },
    // Pusingan sentiasa julat berlabuh pada pusingan pertama — pengguna hanya
    // memilih hujungnya (01 - 01 … 01 - 12), jadi permulaan 02 mustahil dari binaan.
    roundRangeItems() {
      const pad = n => String(n).padStart(2, "0");
      const lo = this.roundItems.length ? this.roundItems[0] : null;
      return this.roundItems.map(p => ({ text: `${pad(lo)} - ${pad(p)}`, value: p }));
    },
    // One dropdown, so the start can never drift off the first pusingan.
    roundRange: {
      get() { return this.roundTo; },
      set(v) {
        if (this.roundItems.length) this.roundFrom = this.roundItems[0];
        this.roundTo = v;
      },
    },
    ddLabel() {
      if (this.allRounds) return "Semua Pusingan";
      if (!this.selectedRoundsModel.length) return "Tiada dipilih";
      const lo = this.selectedRounds[0];
      const hi = this.selectedRounds[this.selectedRounds.length - 1];
      return lo === hi ? `Pusingan ${lo}` : `Pusingan ${lo}–${hi}`;
    },
    // sampel filter no longer shown — replaced by the Wajaran / Tanpa Wajaran toggle
    // sampelLabel() { return this.sampel === "3" ? "Semua Sampel" : `Sampel ${this.sampel}`; },
    // Wajaran / Tanpa Wajaran label for the summary + exports.
    wajaranLabel() { return this.dataWajaran === "TW" ? "Tanpa Wajaran" : "Wajaran"; },
    // Meaningful export filename (no extension) from the active filters, e.g.
    //   Daerah_Strata_Johor_Pusingan1_Wajaran_20260618
    // Pusingan slug for filenames: always a range — "Pusingan2" or "Pusingan1-12"
    // (incl. when all rounds are selected). Falls back to the full available range.
    pusinganSlug() {
      const rounds = this.selectedRounds.length ? this.selectedRounds : this.roundItems;
      if (!rounds.length) return "Pusingan";
      const lo = rounds[0];
      const hi = rounds[rounds.length - 1];
      return "Pusingan" + (lo === hi ? `${lo}` : `${lo}-${hi}`);
    },
    exportFileName() {
      const parts = ["Pendapatan_Isi_Rumah_Bulanan_Purata_dan_Penengah_mengikut_Daerah_Pentadbiran_dan_Strata"];
      parts.push((this.scopeName || "").replace(/[^A-Za-z0-9]+/g, ""));   // negeri always set
      parts.push(this.pusinganSlug);
      parts.push(this.dataWajaran === "TW" ? "TanpaWajaran" : "Wajaran");
      parts.push(this.todayStr.replace(/-/g, ""));
      return parts.join("_");
    },
    scopeName() { return this.negeriName[this.negeri] || this.negeri; },
    withCagr() {
      const pairs = this.cagrPairs;
      return row => ({ ...row, cagr: cagrMapByStrata(row.strata || {}, pairs) });
    },
    sections() {
      const wc = this.withCagr;
      return [
        { key: "purata", title: "Pendapatan Purata", rows: (this.api.purata || []).map(wc) },
        { key: "penengah", title: "Pendapatan Penengah", rows: (this.api.penengah || []).map(wc) },
      ].filter(s => s.rows.length);
    },
    filterSummary() {
      const rd = this.allRounds ? "Semua pusingan" : "Pusingan " + this.selectedRounds.join(",");
      return `${this.scopeName} • ${rd} • ${this.wajaranLabel}`;
    },
  },
  watch: {
    negeri() { this.queueFetch(); },
    sampel() { this.queueFetch(); },
    dataWajaran() { this.queueFetch(); },
    roundFrom(v) { if (v != null && this.roundTo != null && this.roundTo < v) this.roundTo = v; },
    selectedRoundsModel() { this.queueFetch(); },
  },
  async created() {
    loadNegeriList(); // refresh negeri names from lookupnegeri (shared cache)
    // Restricted roles (PP/PO/FE) are pinned to their own negeri — lock the selector
    // and default it to their kodneg before the first fetch. (The backend enforces
    // this too, so the data is scoped regardless; this is the matching UI.)
    const lock = negeriLock(this.$store);
    if (lock.locked) {
      this.negeri = lock.kodneg;
      this.negeriLocked = true;
    }
    // load the active-pusingan filter first (status == 1), then fetch the table
    await this.loadActiveRounds();
    this.fetchData();
  },
  methods: {
    queueFetch() { clearTimeout(this._fetchTimer); this._fetchTimer = setTimeout(() => this.fetchData(), 0); },
    async fetchData() {
      this.loading = true;
      try {
        // empty pusingan selection = explicit "none" (marker), not "all".
        const P = this.selectedRoundsModel.length === 0 ? "__NONE__" : this.selectedRounds.join(",");
        const params = { NG: this.negeri, P, S: this.sampel, W: this.dataWajaran };
        const d = await this.svc.getDaerahStrata(params);
        this.years = (d.years || []).map(Number);
        this.cagrPairs = cagrPairsFrom(this.years);
        this.api = d;
      } catch (err) {
        this.api = {}; this.years = []; this.cagrPairs = [];
      }
      this.loading = false;
    },
    rm(n) {
      if (n === null || n === undefined) return "—";
      return new Intl.NumberFormat("en-MY").format(Math.round(n));
    },
    pf(n) {
      if (n === null || n === undefined) return "—";
      return n.toFixed(1);
    },
    dataCells(r) {
      const cells = [];
      this.years.forEach(y => {
        cells.push({ key: "b" + y, text: this.rm(r.strata.Bandar[y]) });
        cells.push({ key: "l" + y, text: this.rm(r.strata["Luar Bandar"][y]) });
        cells.push({ key: "t" + y, text: this.rm(r.strata.Jumlah[y]) });
      });
      this.cagrExportCols.forEach((c, i) => {
        const v = c.value(r);
        const empty = v === null || v === undefined;
        cells.push({ key: "c" + i, text: empty ? "—" : this.pf(v), empty });
      });
      return cells;
    },
    selectFullRange() {
      if (!this.roundItems.length) return;
      this.roundFrom = this.roundItems[0];
      this.roundTo = this.roundItems[this.roundItems.length - 1];
    },
    resetFilters() {
      // This page requires a negeri, so the default is Johor — but a locked role
      // (PP/PO/FE) must stay on its own kodneg, otherwise the reset would switch
      // them to Johor and fetchData() would request another state explicitly.
      if (!this.negeriLocked) this.negeri = "01";
      this.sampel = "3";
      this.selectFullRange();
    },
    // Load the active pusingan (status == 1) and default-select them all. Mirrors
    // the Wajaran view's getAllPusingan() + status filter. Falls back to 1..12.
    async loadActiveRounds() {
      let rounds = Array.from({ length: 12 }, (_, i) => i + 1);
      try {
        const res = await pusinganSvc.getAllPusingan();
        const rows = (res && res.data && res.data.data) || [];
        const active = rows
          .filter(r => r.status == "1" || r.status == 1)
          .map(r => parseInt(r.pusingan, 10))
          .filter(n => !isNaN(n));
        const uniq = [...new Set(active)].sort((a, b) => a - b);
        if (uniq.length) rounds = uniq;
      } catch (e) {
        console.error("loadActiveRounds failed:", e);
      }
      this.roundItems = rounds;
      this.selectFullRange();
    },

    /* ---- styled .xlsx export ---- */
    async exportExcel() {
      this.exporting = true;
      try {
        await loadTheme();
        await loadScript("/js/exceljs.min.js");
        const ExcelJS = window.ExcelJS;
        const NAVY = argb(T.tajukLaporan), HEAD = argb(T.kepalaJadual), NAVY2 = argb(dark(T.kepalaJadual)), TEAL = argb(T.graf), WHITE = "FFFFFFFF",
              GROUP = "FFE8EEF3", SUB = "FFF4F8FA", LINE = "FFE2E8F0";
        const thin = { style: "thin", color: { argb: LINE } };
        const border = { top: thin, left: thin, bottom: thin, right: thin };
        const center = { vertical: "middle", horizontal: "center", wrapText: true };
        const fill = argb => ({ type: "pattern", pattern: "solid", fgColor: { argb } });

        const YEARS = this.years, CAGR_PAIRS = this.cagrPairs;
        const CAGR_COLS = this.cagrExportCols;
        const STRATA_LABELS = STRATA_KEYS;
        const dataCols = YEARS.length * 3 + CAGR_COLS.length;
        const nCols = 1 + dataCols, L = colLetter, last = L(nCols);
        const MIN_BANNER_COLS = 6;
        const bannerCols = Math.max(nCols, MIN_BANNER_COLS);
        const bannerLast = L(bannerCols);
        const firstCagrIdx = 2 + YEARS.length * 3;

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("Daerah x Strata", { views: [{ showGridLines: false }] });
        const colWidths = [{ width: 24 }, ...Array(YEARS.length * 3).fill({ width: 14 }), ...CAGR_COLS.map(() => ({ width: 13 }))];
        while (colWidths.length < bannerCols) colWidths.push({ width: 12 });
        ws.columns = colWidths;

        // ── Logo row 1: Jata Negara crest + config-driven title (services/exportHeader) ──
        await addJataHeaderExcel(wb, ws, colWidths, bannerLast);

        // ── Title block (rows 2-3, below the logo) ───────────────────────────
        ws.mergeCells(`A2:${bannerLast}2`);
        Object.assign(ws.getCell("A2"), {
          value: await surveyTitle(),
          alignment: center, font: { bold: true, size: 12, color: { argb: WHITE } }, fill: fill(NAVY),
        });
        ws.getRow(2).height = 24;

        ws.mergeCells(`A3:${bannerLast}3`);
        Object.assign(ws.getCell("A3"), {
          value: "PENDAPATAN ISI RUMAH BULANAN MENGIKUT DAERAH PENTADBIRAN DAN STRATA",
          alignment: center, font: { bold: true, size: 11, color: { argb: WHITE } }, fill: fill(TEAL),
        });
        ws.getRow(3).height = 20;

        // Filter summary — ONE line spanning the full width (matches the PDF layout).
        ws.mergeCells(`A5:${bannerLast}5`);
        Object.assign(ws.getCell("A5"), {
          value: `Pilihan: ${this.scopeName}     |     Pusingan: ${this.ddLabel}     |     Data: ${this.wajaranLabel}`,
          font: { size: 10 }, alignment: { vertical: "middle", horizontal: "left" },
        });
        ws.getRow(5).height = 18;

        const setH = (cell, text, bg) => Object.assign(ws.getCell(cell), {
          value: text, alignment: center, border,
          font: { bold: true, size: 10, color: { argb: WHITE } }, fill: fill(bg || HEAD),
        });
        // header rows HEAD1 (year/cagr banner), HEAD2 (strata sub) — after the
        // logo row + title block + 1-line summary. Daerah merged across both.
        const HEAD1 = 7, HEAD2 = 8;
        ws.mergeCells(`A${HEAD1}:A${HEAD2}`); setH(`A${HEAD1}`, "DAERAH PENTADBIRAN");
        YEARS.forEach((y, i) => {
          const c0 = L(2 + i * 3), c2 = L(2 + i * 3 + 2);
          ws.mergeCells(`${c0}${HEAD1}:${c2}${HEAD1}`); setH(`${c0}${HEAD1}`, `${y} (RM)`);
          setH(`${L(2 + i * 3)}${HEAD2}`, "Bandar", NAVY2);
          setH(`${L(2 + i * 3 + 1)}${HEAD2}`, "Luar Bandar", NAVY2);
          setH(`${L(2 + i * 3 + 2)}${HEAD2}`, "Jumlah", NAVY2);
        });
        const cagrBanners = CAGR_PAIRS.length
          ? CAGR_PAIRS.map(p => `CAGR ${p.from} - ${p.to} (%)`)
          : ["KADAR PERTUMBUHAN TAHUNAN DIKOMPAUN (CAGR) (%)"];
        cagrBanners.forEach((label, gi) => {
          const c0 = L(firstCagrIdx + gi * 3), c2 = L(firstCagrIdx + gi * 3 + 2);
          ws.mergeCells(`${c0}${HEAD1}:${c2}${HEAD1}`); setH(`${c0}${HEAD1}`, label);
          STRATA_LABELS.forEach((s, si) => setH(`${L(firstCagrIdx + gi * 3 + si)}${HEAD2}`, s, NAVY2));
        });
        ws.getRow(HEAD1).height = 30; ws.getRow(HEAD2).height = 20;

        // body
        let rowIdx = HEAD2 + 1;
        this.sections.forEach(sec => {
          ws.mergeCells(`A${rowIdx}:${last}${rowIdx}`);
          Object.assign(ws.getCell(`A${rowIdx}`), {
            value: sec.title.toUpperCase(), border,
            alignment: { vertical: "middle", horizontal: "left" },
            font: { bold: true, size: 10, color: { argb: "FF0B1F3A" } }, fill: fill(GROUP),
          });
          ws.getRow(rowIdx).height = 18; rowIdx++;

          sec.rows.forEach(r => {
            const row = ws.getRow(rowIdx);
            const isTotal = !!r.isTotal;
            const vals = [r.daerah];
            YEARS.forEach(y => { vals.push(r.strata.Bandar[y], r.strata["Luar Bandar"][y], r.strata.Jumlah[y]); });
            CAGR_COLS.forEach(c => vals.push(c.value(r)));
            vals.forEach((val, ci) => {
              const cell = row.getCell(ci + 1);
              const isCagrCol = ci >= firstCagrIdx - 1;
              // any value column (RM or CAGR) renders "—" when null/missing
              if (ci !== 0 && (val === null || val === undefined)) cell.value = "—";
              else if (typeof val === "number") cell.value = isCagrCol ? Math.round(val * 10) / 10 : Math.round(val);
              else cell.value = val;
              cell.border = border;
              cell.alignment = ci === 0 ? { vertical: "middle", horizontal: "left", indent: isTotal ? 0 : 1 } : { vertical: "middle", horizontal: "right" };
              cell.font = { size: 11, bold: ci === 0 && isTotal };
              if (ci >= 1 && !isCagrCol && typeof cell.value === "number") cell.numFmt = '#,##0';
              if (isCagrCol && typeof cell.value === "number") cell.numFmt = '0.0';
            });
            if (isTotal) for (let c = 1; c <= nCols; c++) { row.getCell(c).fill = fill(TEAL); row.getCell(c).font = { size: 11, bold: true, color: { argb: WHITE } }; }
            else for (let c = 1; c <= nCols; c++) row.getCell(c).fill = fill(SUB);
            rowIdx++;
          });
          rowIdx++;
        });

        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${this.exportFileName}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        this.snackbar = { show: true, message: err.message || "Eksport Excel gagal.", color: "error", timeout: 4000 };
      }
      this.exporting = false;
    },

    /* ---- styled PDF export ---- */
    async exportPDF() {
      this.exportingPdf = true;
      try {
        await loadTheme();
        await loadScript("/js/jspdf.umd.min.js");
        await loadScript("/js/jspdf.plugin.autotable.min.js");
        const { jsPDF } = window.jspdf;
        const NAVY = rgb(T.tajukLaporan), HEAD = rgb(T.kepalaJadual), NAVY2 = rgb(dark(T.kepalaJadual)), TEAL = rgb(T.graf), GROUP = [232, 238, 243];

        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        // ── Watermark (faded HIES logo) — painted FIRST so the title and table
        //    render on top of it. jsPDF has no z-order: paint order is stacking
        //    order, so "behind" = drawn before. 0.14 keeps the blue/green visible.
        const wm = await loadImage(hiesWatermark, 0.14);
        const wmH = 110; // mm
        const wmW = wm.width * (wmH / wm.height);
        const drawWatermark = () =>
          doc.addImage(wm.dataUrl, "PNG", (pageW - wmW) / 2, (pageH - wmH) / 2, wmW, wmH);
        drawWatermark(); // page 1 — later pages via autoTable's willDrawPage hook

        // ── Logo: Jata Negara crest + config-driven title (services/exportHeader) ──
        await addJataHeaderPdf(doc, pageW);

        doc.setFont("times", "bold"); doc.setFontSize(12); doc.setTextColor(NAVY[0], NAVY[1], NAVY[2]);
        doc.text(await surveyTitle(), pageW / 2, 35, { align: "center" });
        doc.setFontSize(10); doc.setTextColor(TEAL[0], TEAL[1], TEAL[2]);
        doc.text("PENDAPATAN BULANAN MENGIKUT DAERAH PENTADBIRAN & STRATA", pageW / 2, 41.5, { align: "center" });

        doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(90);
        doc.text(
          `Pilihan: ${this.scopeName}     |     Pusingan: ${this.ddLabel}     |     Data: ${this.wajaranLabel}     |     Dijana: ${new Date().toLocaleString("en-MY")}`,
          14, 48
        );

        const YEARS = this.years, CAGR_PAIRS = this.cagrPairs;
        const CAGR_COLS = this.cagrExportCols, nCols = this.colCount;
        const cagrBanners = CAGR_PAIRS.length
          ? CAGR_PAIRS.map(p => `CAGR ${p.from}-${p.to} (%)`)
          : ["CAGR (%)"];
        const head = [
          [
            { content: "DAERAH PENTADBIRAN", rowSpan: 2 },
            ...YEARS.map(y => ({ content: `${y} (RM)`, colSpan: 3 })),
            ...cagrBanners.map(label => ({ content: label, colSpan: 3 })),
          ],
          [
            ...YEARS.flatMap(() => ["Bandar", "Luar Bandar", "Jumlah"]),
            ...cagrBanners.flatMap(() => ["Bandar", "Luar Bandar", "Jumlah"]),
          ],
        ];

        const kinds = [];
        const body = [];
        this.sections.forEach(sec => {
          kinds.push("group"); body.push([{ content: sec.title.toUpperCase(), colSpan: nCols }]);
          sec.rows.forEach(r => {
            kinds.push(r.isTotal ? "total" : "sub");
            body.push([
              r.daerah,
              ...YEARS.flatMap(y => [this.rm(r.strata.Bandar[y]), this.rm(r.strata["Luar Bandar"][y]), this.rm(r.strata.Jumlah[y])]),
              ...CAGR_COLS.map(c => { const v = c.value(r); return (v === null || v === undefined) ? "—" : this.pf(v); }),
            ]);
          });
        });

        doc.autoTable({
          head, body, startY: 52,
          // pages the table adds get their watermark painted before their content
          // too (page 1 already has it — drawn above, before the header block)
          willDrawPage: d => { if (d.pageNumber > 1) drawWatermark(); },
          // fillColor:false = TRANSPARENT body cells, so the watermark painted
          // beneath the table shows through (the default theme paints every cell
          // white, which was hiding it). Meaningful fills (totals, group rows)
          // are still applied opaquely in didParseCell.
          theme: "plain",
          styles: { font: "helvetica", fontSize: 6.8, cellPadding: 1.3, halign: "right", lineColor: [226, 232, 240], lineWidth: 0.1, valign: "middle", fillColor: false },
          headStyles: { fillColor: HEAD, textColor: 255, halign: "center", fontSize: 6.5, valign: "middle" },
          columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
          didParseCell: d => {
            if (d.section === "head" && d.row.index === 1) d.cell.styles.fillColor = NAVY2;
            if (d.section === "body") {
              const kind = kinds[d.row.index];
              if (kind === "group") { d.cell.styles.fillColor = GROUP; d.cell.styles.textColor = [11, 31, 58]; d.cell.styles.fontStyle = "bold"; d.cell.styles.halign = "left"; }
              else if (kind === "total") { d.cell.styles.fillColor = TEAL; d.cell.styles.textColor = 255; d.cell.styles.fontStyle = "bold"; }
              // "sub" rows: no fill — transparent like the plain rows, so the
              // watermark shows through the daerah breakdown rows too
              else if (kind === "sub" && d.column.index === 0) d.cell.styles.fontStyle = "normal";
            }
          },
        });

        // page numbers (e.g. "1/10") bottom-right on every page — the watermark
        // is painted per page BEFORE the content (see drawWatermark above)
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(120);
        for (let i = 1; i <= pageCount; i++) { doc.setPage(i); doc.text(`${i}/${pageCount}`, pageW - 14, pageH - 6, { align: "right" }); }

        doc.save(`${this.exportFileName}.pdf`);
      } catch (err) {
        this.snackbar = { show: true, message: err.message || "Eksport PDF gagal.", color: "error", timeout: 4000 };
      }
      this.exportingPdf = false;
    },
  },
};
</script>

<style scoped>
/* ---------- Bar penapis ----------
   Warna daripada palet yang dikongsi `paparanutama`, sidebar (MenuHeader) dan
   <PageHeader>: teal-700 #037769, wash #eef8f6, hair #dbeeeb. */
.filter-bar {
  background: #f7fcfb;
  border-top: 1px solid #dbeeeb;
  border-bottom: 1px solid #dbeeeb;
}

/* Label penapis — sama gaya dengan `.nav-eyebrow` dalam sidebar. */
.fld__label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}

/* Medan outlined: birai teal lembut, menyerlah teal-700 bila fokus. */
.filter-bar ::v-deep .v-input fieldset {
  border-color: #cfeae7;
}
.filter-bar ::v-deep .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.filter-bar ::v-deep .v-select__selection--comma {
  color: #21403c;
}
.filter-bar ::v-deep .v-input__append-inner .v-icon {
  color: #7aa8a2;
}

/* Butang "Tetapkan Semula" — dulu kelabu neutral; kini senada teal. */
.reset-btn.v-btn--outlined {
  border-color: #cfeae7;
  color: #037769;
  letter-spacing: normal;
}
.reset-btn.v-btn--outlined:hover {
  background: #eef8f6;
  border-color: #037769;
}

/* Compact Pusingan range select — one dropdown, "01 - 01" … "01 - 12" */
.pusingan-range .pusingan-sel {
  width: 116px;
  flex: 0 0 116px;
}
.pusingan-range ::v-deep .v-select__selections {
  flex-wrap: nowrap;
}
.pusingan-range ::v-deep .v-select__selection {
  overflow: visible;
  white-space: nowrap;
}
.pusingan-range ::v-deep .v-input__slot {
  padding: 0 8px !important;
}
.pusingan-range ::v-deep .v-input__append-inner {
  margin-left: 0;
}

.daerah-strata {
  padding: 16px;
  margin-left: -12px;
  margin-right: -12px;
}
/* Toggle Wajaran / Tanpa Wajaran: birai sepadan dengan medan outlined di
   sebelahnya; pilihan aktif = teal pejal (dulu biru `primary` Vuetify). */
.data-toggle {
  border: 1px solid #cfeae7;
  border-radius: 4px;
  overflow: hidden;
  background: #ffffff;
}
.data-toggle ::v-deep .v-btn {
  border: none !important;
  letter-spacing: normal;
  color: #4a8a84;
  background: transparent;
}
.data-toggle ::v-deep .v-btn.v-btn--active {
  background: #037769 !important;
  color: #ffffff !important;
}
/* Vuetify meletakkan lapisan legap pada butang aktif — ia mengaburkan teal. */
.data-toggle ::v-deep .v-btn.v-btn--active .v-btn__overlay,
.data-toggle ::v-deep .v-btn.v-btn--active::before {
  opacity: 0 !important;
}

::v-deep .ks-table > .v-data-table__wrapper { overflow-x: auto; }
::v-deep .ks-table table { border-collapse: separate; border-spacing: 0; width: 100%; font-size: var(--fs-sm); white-space: nowrap; min-width: 1000px; }
::v-deep .ks-table thead th {
  background: var(--warna-kepala-jadual, #1B7E91); color: #fff !important; font-weight: 600; padding: 7px 10px;
  text-align: center !important; border: 1px solid #2a8ea1; vertical-align: middle;
  font-size: var(--fs-sm) !important; line-height: 1.3; position: sticky; top: 0; z-index: 4;
}
::v-deep .ks-table thead th.strata-sub,
/* Satu takuk LEBIH KECIL daripada baris kepala di atasnya (--fs-xs): itulah
   yang menandakan baris ini sebagai sub-kepala. Jika kedua-duanya sama saiz,
   dua baris kepala itu terbaca sebagai satu blok. */
::v-deep .ks-table thead th.cagr-sub { background: var(--warna-kepala-jadual-2, #15606F); font-size: var(--fs-xs) !important; }
::v-deep .ks-table thead th.cat { left: 0; z-index: 6; }
::v-deep .ks-table tbody td {
  padding: 9px 10px; border: 1px solid #E2E8F0; text-align: right; color: #0B1F3A; font-size: var(--fs-sm);
}
::v-deep .ks-table tbody td.cat {
  text-align: left; font-weight: 600; position: sticky; left: 0; background: #fff; z-index: 2;
}
::v-deep .ks-table tbody td.cagr-empty { text-align: center; color: #94A3B8; }
::v-deep .ks-table tbody tr:hover td { background: #EFF7F7 !important; }
::v-deep .ks-table tbody tr.total td { background: var(--warna-graf, #0EA5A4) !important; color: #fff; font-weight: 700; border-color: var(--warna-graf-gelap, #0B827F); }
::v-deep .ks-table tbody tr.total td.cat { background: var(--warna-graf, #0EA5A4) !important; color: #fff; }
::v-deep .ks-table tbody tr.section td.cat {
  background: #E8EEF3 !important; color: #0B1F3A; font-weight: 700; text-transform: uppercase;
  letter-spacing: .4px; font-size: var(--fs-xs); text-align: left; position: static;
}
::v-deep .ks-table tbody tr.sub td { background: #F4F8FA !important; }
::v-deep .ks-table tbody tr.sub td.cat { background: #F4F8FA !important; padding-left: 24px; font-weight: 600; }
::v-deep .ks-table tbody tr.spacer td { background: #475569 !important; padding: 0; height: 6px; border: none; }
</style>
